import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Typography,
  Form,
  DatePicker,
  Select,
  Input,
  Space,
  Button,
  Alert,
  Divider,
  Spin,
  message,
  Upload,
} from "antd";
import { CalendarOutlined, FieldTimeOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import enGB from "antd/locale/en_GB";
import updateLocale from "dayjs/plugin/updateLocale";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAlltype } from "../../slices/leavetype";
import { addLeave, editLeaveCurrentUser, getMyLeaves } from "../../slices/leave";
import getAccess from "../../utilities/getAccess";
import "./_leaveRequest.scss";

// Configure dayjs to start week on Monday
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1, // 1 = Monday, 0 = Sunday
});

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const ApplyForLeaveModal = ({
  title,
  id,
  open,
  handleClose,
  data,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [certificateFiles, setCertificateFiles] = useState([]);
  const [calendarRange, setCalendarRange] = useState([]);
  const [autoEndDate, setAutoEndDate] = useState(null);
  const [defaultPickerValue, setDefaultPickerValue] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);

  const user = useSelector((state) => state.user.fetchMe);
  const leaveTypes = useSelector(
    (state) => state?.leaveTypes?.leaveTypes || []
  );
  const leaveTypeStatus = useSelector(
    (state) => state?.leaveTypes?.leaveTypeStatus || "idle"
  );
  const myLeaves = useSelector((state) => {
    const leavesData = state?.leave?.getMyLeaves;
    // Handle both array and object response formats
    if (Array.isArray(leavesData)) {
      return leavesData;
    }
    // If it's an object with a leaves property, return that
    if (leavesData && leavesData.leaves && Array.isArray(leavesData.leaves)) {
      return leavesData.leaves;
    }
    // Otherwise return empty array
    return [];
  });

  const getLeaveTypeSystemCode = useCallback((type) => {
    if (!type) return "";
    const rawCode = (type.code || "").toString().trim();
    if (rawCode) return rawCode.toUpperCase();
    // Requirement: never classify/verify using `name`.
    return "";
  }, []);

  const buildCertificateUrl = useCallback((certificatePath) => {
    if (!certificatePath) return '';
    const baseUrl = process.env.REACT_APP_IMG_URL || '';
    const sanitizedBase = baseUrl.replace(/\/img\/?$/, '');
    const normalizedPath = certificatePath.startsWith('medical-certificates/')
      ? certificatePath
      : `medical-certificates/${certificatePath}`;
    return `${sanitizedBase}/${normalizedPath}`;
  }, []);

  const extractCertificatePath = useCallback((file) => {
    if (file?.certificatePath) {
      return file.certificatePath;
    }
    if (file?.url) {
      const split = file.url.split('/medical-certificates/');
      if (split.length > 1) {
        return `medical-certificates/${split[1]}`;
      }
    }
    return null;
  }, []);

  useEffect(() => {
    if (open) {
      dispatch(fetchAlltype());
      dispatch(getMyLeaves());
    }
  }, [open, dispatch]);

  // Check if editing
  const isEdit = useMemo(() => {
    return data && (data.isEdit || data._id);
  }, [data]);

  useEffect(() => {
    if (open && data && isEdit) {
      // Populate form with existing leave data for editing
      const leaveTypeId = data?.type?._id || data?.type?.id || "";
      const startDate = data?.dateStart ? dayjs(data.dateStart) : null;
      const endDate = data?.dateEnd ? dayjs(data.dateEnd) : null;
      
      form.setFieldsValue({
        type: leaveTypeId,
        dateRange: startDate && endDate ? [startDate, endDate] : null,
        description: data?.description || "",
      });
      
      setSelectedLeaveType(
        leaveTypes.find(
          (type) => (type?._id || type?.id) === leaveTypeId
        ) || null
      );
      setCalendarRange(startDate && endDate ? [startDate, endDate] : []);
      
      // Set medical certificates if they exist
      if (data?.medicalCertificates && Array.isArray(data.medicalCertificates)) {
        setCertificateFiles(
          data.medicalCertificates.map((cert, index) => ({
            uid: `existing-${index}`,
            name: `certificate-${index + 1}`,
            status: 'done',
            url: buildCertificateUrl(cert),
            certificatePath: cert,
          }))
        );
      }
    } else if (!open) {
      setSelectedLeaveType(null);
      setCertificateFiles([]);
      setCalendarRange([]);
      setAutoEndDate(null);
      setDefaultPickerValue(null);
      setSelectedStartDate(null);
      form.resetFields();
    }
  }, [open, data, isEdit, form, leaveTypes]);

  const approvedLeaves = useMemo(
    () =>
      (myLeaves || []).filter(
        (leave) => {
          const statusCode = (leave?.status?.code || "").toString().toLowerCase();
          return statusCode === "approved";
        }
      ),
    [myLeaves]
  );

  const leaveUsageByType = useMemo(() => {
    const usage = {};
    approvedLeaves.forEach((leave) => {
      const typeName = leave?.type?.name || "Leave";
      // Use leaveLength if available, otherwise calculate from dates
      const days = leave?.leaveLength || (() => {
        const start = dayjs(leave?.dateStart);
        const end = dayjs(leave?.dateEnd);
        return end.diff(start, "day") + 1;
      })();
      usage[typeName] = (usage[typeName] || 0) + (days > 0 ? days : 0);
    });
    return usage;
  }, [approvedLeaves]);

  // Leave types for display (includes system types for statistics)
  const displayableLeaveTypes = useMemo(() => {
    if (!Array.isArray(leaveTypes)) {
      return [];
    }
    return leaveTypes.filter((type) => {
      const name = (type?.name || "").toLowerCase().trim();
      return true;
    });
  }, [leaveTypes]);

  const selectedLeaveTypeCode = useMemo(
    () => getLeaveTypeSystemCode(selectedLeaveType),
    [selectedLeaveType, getLeaveTypeSystemCode]
  );

  const isSickLeave = selectedLeaveTypeCode === "SICK";
  const parentalTypeCode = "PARENTAL";
  const isParentalType = selectedLeaveTypeCode === parentalTypeCode;
  const isFemaleUser = (user?.gender || "").toLowerCase() === "female";
  // For backward compatibility in the component, keep the name "isMaternity" but
  // interpret it as "extended parental leave for female employees".
  const isMaternity = isParentalType && isFemaleUser;
  const isParentalForMale = isParentalType && !isFemaleUser;
  const sickBalance =
    user?.sickLeaveBalance != null ? user.sickLeaveBalance : 10;

  // Leave types for selection dropdown (excludes system types)
  const selectableLeaveTypes = useMemo(() => {
    if (!Array.isArray(leaveTypes)) {
      return [];
    }
    const systemCodes = [];
    const hasSickBalance = sickBalance > 0;
    return leaveTypes.filter((type) => {
      const code = getLeaveTypeSystemCode(type);
      
      if (systemCodes.includes(code)) {
        return false;
      }
      
      // Hide "Sick" leave type when user has 0 sick leave balance
      if (code === "SICK" && !hasSickBalance) {
        return false;
      }
      
      return true;
    });
  }, [leaveTypes, sickBalance, getLeaveTypeSystemCode]);

  const leaveTypeOptions = useMemo(
    () =>
      selectableLeaveTypes.map((type) => {
        const name = type?.name || "Leave";
        const code = getLeaveTypeSystemCode(type);
        let maxDays = type?.maxDays;
        if (typeof maxDays !== "number" && code === "SICK") {
          maxDays = 10;
        }

        let label = name;
        if (typeof maxDays === "number") {
          const dayWord = maxDays === 1 ? "يوم" : "أيام";
          label = `${name} (الحد الأقصى سنويًا ${maxDays} ${dayWord})`;
        } else if (code === "PARENTAL") {
          if (isFemaleUser) {
            label = `${name} (بحدٍّ أقصى إجازتَي أبوّة في السنة)`;
          } else {
            const parentalDaysNonFemale = 7;
            const dayWord =
              parentalDaysNonFemale === 1 ? "يوم" : "أيام";
            label = `${name} (الحد الأقصى سنويًا ${parentalDaysNonFemale} ${dayWord})`;
          }
        }

        return { label, value: type?._id || type?.id };
      }),
    [selectableLeaveTypes, getLeaveTypeSystemCode, isFemaleUser]
  );

  const leaveTypeMap = useMemo(() => {
    const map = new Map();
    selectableLeaveTypes.forEach((type) => {
      if (type?._id) {
        map.set(type._id, type);
      }
      if (type?.id) {
        map.set(type.id, type);
      }
    });
    return map;
  }, [selectableLeaveTypes]);

  const maternityLeaves = useMemo(() => {
    // Treat "maternityLeaves" as parental leaves in the data model
    return (myLeaves || []).filter(
      (leave) => getLeaveTypeSystemCode(leave?.type) === parentalTypeCode
    );
  }, [myLeaves, getLeaveTypeSystemCode]);

  const activeMaternityLeaves = useMemo(() => {
    return maternityLeaves
      .filter((leave) => {
        const statusCode = (leave?.status?.code || "").toLowerCase();
        return statusCode !== "rejected";
      })
      .sort(
        (a, b) =>
          dayjs(a.dateStart).valueOf() - dayjs(b.dateStart).valueOf()
      );
  }, [maternityLeaves]);

  // Separate approved and waiting maternity leaves
  const approvedMaternityLeaves = useMemo(() => {
    return activeMaternityLeaves.filter((leave) => {
      const statusCode = (leave?.status?.code || "").toLowerCase();
      return statusCode === "approved";
    });
  }, [activeMaternityLeaves]);

  // Get approved maternity leaves for the selected year (for females, check by year like males)
  const approvedMaternityLeavesForYear = useMemo(() => {
    if (!isMaternity || !selectedStartDate || isEdit) {
      return approvedMaternityLeaves;
    }
    
    const requestedYear = selectedStartDate.year();
    return approvedMaternityLeaves.filter((leave) => {
      const leaveStart = dayjs(leave.dateStart);
      return leaveStart.year() === requestedYear;
    });
  }, [isMaternity, selectedStartDate, approvedMaternityLeaves, isEdit]);

  const waitingMaternityLeaves = useMemo(() => {
    return activeMaternityLeaves.filter((leave) => {
      const statusCode = (leave?.status?.code || "").toLowerCase();
      return statusCode === "waiting-tech-approval" || statusCode === "waiting-hr-approval";
    });
  }, [activeMaternityLeaves]);

  // When editing, exclude the current leave from the count to allow editing existing leaves
  const activeMaternityLeavesForLimit = useMemo(() => {
    if (!isEdit || !data?._id) {
      return activeMaternityLeaves;
    }
    // Exclude the current leave being edited from the count
    return activeMaternityLeaves.filter((leave) => leave._id !== data._id);
  }, [activeMaternityLeaves, isEdit, data?._id]);

  // When editing, exclude the current leave from waiting leaves count
  const waitingMaternityLeavesForLimit = useMemo(() => {
    if (!isEdit || !data?._id) {
      return waitingMaternityLeaves;
    }
    return waitingMaternityLeaves.filter((leave) => leave._id !== data._id);
  }, [waitingMaternityLeaves, isEdit, data?._id]);

  // Use approved leaves for the selected year to determine first/second maternity
  // For females, we check by year (like males) - they can take first maternity in 2025 and another first in 2027
  // Only approved leaves have confirmed end dates we can use for calculations
  const firstMaternityLeaveForYear = useMemo(() => {
    if (!selectedStartDate) {
      // If no date selected yet, use all approved leaves (for initial state)
      return approvedMaternityLeaves[0] || null;
    }
    return approvedMaternityLeavesForYear[0] || null;
  }, [selectedStartDate, approvedMaternityLeavesForYear, approvedMaternityLeaves]);
  
  const firstMaternityEnd = useMemo(() => {
    return firstMaternityLeaveForYear ? dayjs(firstMaternityLeaveForYear.dateEnd) : null;
  }, [firstMaternityLeaveForYear]);
  
  const secondRequestDeadline = useMemo(() => {
    return firstMaternityEnd ? firstMaternityEnd.subtract(1, "month") : null;
  }, [firstMaternityEnd]);
  
  const earliestSecondStart = useMemo(() => {
    return firstMaternityEnd ? firstMaternityEnd.add(1, "day") : null;
  }, [firstMaternityEnd]);

  // Determine first/second based on approved leaves for the selected year
  // This allows females to take first maternity in 2025 and another first in 2027
  const isFirstMaternity = useMemo(() => {
    if (!isMaternity) return false;
    if (!selectedStartDate) {
      // If no date selected yet, check all approved leaves
      return approvedMaternityLeaves.length === 0;
    }
    return approvedMaternityLeavesForYear.length === 0;
  }, [isMaternity, selectedStartDate, approvedMaternityLeaves.length, approvedMaternityLeavesForYear.length]);
  
  const isSecondMaternity = useMemo(() => {
    if (!isMaternity) return false;
    if (!selectedStartDate) {
      // If no date selected yet, check all approved leaves
      return approvedMaternityLeaves.length === 1;
    }
    return approvedMaternityLeavesForYear.length === 1;
  }, [isMaternity, selectedStartDate, approvedMaternityLeaves.length, approvedMaternityLeavesForYear.length]);
  
  // Prevent creating a second parental leave if there's already one waiting for approval
  const hasWaitingMaternityLeave = isMaternity && waitingMaternityLeavesForLimit.length > 0;
  
  // Use the filtered list when checking if maternity is unavailable (excludes current leave when editing)
  const maternityUnavailable = isMaternity && activeMaternityLeavesForLimit.length >= 2;
  
  // Check if there are any non-approved maternity leaves (waiting for approval)
  const nonApprovedMaternityLeaves = useMemo(() => {
    return waitingMaternityLeaves;
  }, [waitingMaternityLeaves]);

  // Check if user is a team lead whose own leaves go straight to HR
  const isTeamLead = useMemo(() => {
    const roleCode = (user?.role?.code || "").toUpperCase();
    return (
      (roleCode === "TEAM LEAD" || roleCode === "TEAMLEAD") &&
      !user?.employeeInDepartments?.length
    );
  }, [user?.role?.code, user?.employeeInDepartments]);

  // Cancellable leaves: employees can cancel waiting-tech-approval, team leads can cancel waiting-hr-approval
  const cancellableMaternityLeaves = useMemo(() => {
    return waitingMaternityLeaves.filter((leave) => {
      const statusCode = (leave?.status?.code || "").toLowerCase();
      if (isTeamLead) {
        // Team leads can cancel leaves in waiting-hr-approval status
        return statusCode === "waiting-hr-approval";
      } else {
        // Employees can only cancel leaves in waiting-tech-approval status
        return statusCode === "waiting-tech-approval";
      }
    });
  }, [waitingMaternityLeaves, isTeamLead]);

  // Waiting HR approval leaves (excluding cancellable ones to avoid duplication)
  // For team leads: don't show waiting HR approval separately since they're already in cancellable
  // For employees: show waiting HR approval leaves that they cannot cancel
  const waitingHRMaternityLeaves = useMemo(() => {
    const hrLeaves = waitingMaternityLeaves.filter((leave) => {
      const statusCode = (leave?.status?.code || "").toLowerCase();
      return statusCode === "waiting-hr-approval";
    });
    
    if (isTeamLead) {
      // For team leads, exclude cancellable leaves (which are all their HR approval leaves)
      // This prevents duplication since cancellable leaves are shown separately
      const cancellableIds = new Set(cancellableMaternityLeaves.map(leave => leave._id));
      return hrLeaves.filter(leave => !cancellableIds.has(leave._id));
    } else {
      // For employees, show all HR approval leaves (they can't cancel them anyway)
      return hrLeaves;
    }
  }, [waitingMaternityLeaves, isTeamLead, cancellableMaternityLeaves]);
  const isSecondRequestLate =
    isSecondMaternity &&
    secondRequestDeadline &&
    dayjs().isAfter(secondRequestDeadline);
  // When editing, don't block submission even if limit is reached (we're just updating an existing leave)
  // Also block if there's a waiting maternity leave (can't create second while first is pending)
  const maternitySubmissionBlocked =
    (!isEdit && maternityUnavailable) || 
    (!isEdit && hasWaitingMaternityLeave) ||
    (isSecondMaternity && isSecondRequestLate);
  
  // Get stable string representation for earliestSecondStart to use as dependency
  const earliestSecondStartKey = useMemo(() => {
    return earliestSecondStart ? earliestSecondStart.format('YYYY-MM-DD') : null;
  }, [earliestSecondStart]);

  // Get waiting parental leaves for males (similar to waiting maternity leaves for females)
  const waitingParentalLeavesForMale = useMemo(() => {
    if (!isParentalForMale) return [];
    
    return (myLeaves || []).filter((leave) => {
      // Exclude the current leave being edited if editing
      if (isEdit && data?._id && leave._id === data._id) {
        return false;
      }
      const typeCode = getLeaveTypeSystemCode(leave?.type);
      const statusCode = (leave?.status?.code || "").toLowerCase();
      return (
        typeCode === parentalTypeCode &&
        (statusCode === "waiting-tech-approval" || statusCode === "waiting-hr-approval")
      );
    });
  }, [isParentalForMale, myLeaves, isEdit, data?._id, getLeaveTypeSystemCode]);

  // Cancellable parental leaves for males: employees can cancel waiting-tech-approval, team leads can cancel waiting-hr-approval
  const cancellableParentalLeavesForMale = useMemo(() => {
    return waitingParentalLeavesForMale.filter((leave) => {
      const statusCode = (leave?.status?.code || "").toLowerCase();
      if (isTeamLead) {
        // Team leads can cancel leaves in waiting-hr-approval status
        return statusCode === "waiting-hr-approval";
      } else {
        // Employees can only cancel leaves in waiting-tech-approval status
        return statusCode === "waiting-tech-approval";
      }
    });
  }, [waitingParentalLeavesForMale, isTeamLead]);

  // Non-cancellable waiting parental leaves (for display purposes)
  const nonCancellableParentalLeavesForMale = useMemo(() => {
    const cancellableIds = new Set(cancellableParentalLeavesForMale.map(leave => leave._id));
    return waitingParentalLeavesForMale.filter(leave => !cancellableIds.has(leave._id));
  }, [waitingParentalLeavesForMale, cancellableParentalLeavesForMale]);

  // Separate non-cancellable by status for better messaging
  const nonCancellableHRParentalLeaves = useMemo(() => {
    return nonCancellableParentalLeavesForMale.filter((leave) => {
      const statusCode = (leave?.status?.code || "").toLowerCase();
      return statusCode === "waiting-hr-approval";
    });
  }, [nonCancellableParentalLeavesForMale]);

  // Check if male user has a waiting parental leave
  const hasWaitingParentalLeaveForMale = useMemo(() => {
    return !isEdit && isParentalForMale && waitingParentalLeavesForMale.length > 0;
  }, [isEdit, isParentalForMale, waitingParentalLeavesForMale.length]);

  // Check if male user already has a parental leave for the selected year
  const hasExistingParentalLeaveForYear = useMemo(() => {
    if (!isParentalForMale || !selectedStartDate || isEdit) {
      return false;
    }
    
    const requestedYear = selectedStartDate.year();
    const parentalLeavesForRequestedYear = (myLeaves || []).filter((leave) => {
      // Exclude the current leave being edited if editing
      if (isEdit && data?._id && leave._id === data._id) {
        return false;
      }
      const typeCode = getLeaveTypeSystemCode(leave?.type);
      const statusCode = (leave?.status?.code || "").toLowerCase();
      const leaveStart = dayjs(leave.dateStart);
      return (
        typeCode === parentalTypeCode &&
        statusCode !== "rejected" &&
        leaveStart.year() === requestedYear
      );
    });
    
    return parentalLeavesForRequestedYear.length >= 1;
  }, [isParentalForMale, selectedStartDate, myLeaves, isEdit, data?._id, getLeaveTypeSystemCode]);
  
  // Block submission for male parental leave if they already have one for the selected year
  // Block submission for male parental leave if they already have one for the selected year
  // OR if they have a waiting parental leave (can't create new one while one is pending)
  const parentalForMaleBlocked = useMemo(() => {
    return (!isEdit && isParentalForMale && hasExistingParentalLeaveForYear) ||
           (!isEdit && isParentalForMale && hasWaitingParentalLeaveForMale);
  }, [isEdit, isParentalForMale, hasExistingParentalLeaveForYear, hasWaitingParentalLeaveForMale]);

  // Set default picker value to first available date for second maternity leave
  useEffect(() => {
    if (isSecondMaternity && earliestSecondStart) {
      // Set the default picker value to the month of the earliest available start date
      setDefaultPickerValue(prev => {
        if (!prev || !prev.isSame(earliestSecondStart, 'month')) {
          return earliestSecondStart;
        }
        return prev;
      });
    } else if (!isMaternity) {
      setAutoEndDate(null);
      setCalendarRange([]);
      setDefaultPickerValue(null);
    } else if (isFirstMaternity) {
      // For first maternity, use current date or today
      setDefaultPickerValue(prev => {
        const today = dayjs();
        if (!prev || !prev.isSame(today, 'month')) {
          return today;
        }
        return prev;
      });
    }
  }, [isMaternity, isFirstMaternity, isSecondMaternity, earliestSecondStartKey]);

  const handleModalClose = () => {
    if (submitting) return;
    form.resetFields();
    setFeedback(null);
    setSelectedLeaveType(null);
    setCertificateFiles([]);
    setCalendarRange([]);
    setAutoEndDate(null);
    handleClose(id);
  };

  const handleReset = () => {
    form.resetFields();
    setFeedback(null);
    setSelectedLeaveType(null);
    setCertificateFiles([]);
    setCalendarRange([]);
    setAutoEndDate(null);
  };

  const handleCertificateBeforeUpload = (file) => {
    const mimeOk = file.type?.startsWith("image/");
    // Some mobile browsers (often Android) leave MIME type empty; fall back on filename.
    const extOk = /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(file.name || "");
    if (!mimeOk && !extOk) {
      message.error("Only image files are allowed for medical certificates.");
      return Upload.LIST_IGNORE;
    }

    if (certificateFiles.length >= 3) {
      message.warning("You can upload up to 3 certificates.");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleCertificateChange = ({ fileList }) => {
    const normalizedList = fileList.slice(0, 3).map((item, index) => {
      if (item.originFileObj) {
        return item;
      }
      const certificatePath = extractCertificatePath(item);
      return {
        ...item,
        uid: item.uid || `existing-${index}`,
        status: item.status || 'done',
        certificatePath,
        url: buildCertificateUrl(certificatePath || item.url),
      };
    });
    setCertificateFiles(normalizedList);
  };

  const handleCertificateRemove = (file) => {
    // Remove the file from state immediately
    setCertificateFiles((prev) =>
      prev.filter((item) => item.uid !== file.uid)
    );
    // Return false to allow default removal behavior from Upload component
    return false;
  };

  const handleCalendarChange = (dates) => {
    setCalendarRange(dates || []);
    
    // When creating or editing parental leave for males, if a start date is selected (even if end is null),
    // immediately calculate and set the end date to avoid RangePicker restrictions
    if (isParentalForMale && dates && dates[0]) {
      const computedEnd = dates[0].add(6, "day"); // 7 days total including start date
      setAutoEndDate(computedEnd);
      const newRange = [dates[0], computedEnd];
      setCalendarRange(newRange);
      form.setFieldsValue({ dateRange: newRange });
      // Store the selected start date for checking existing parental leaves
      setSelectedStartDate(dates[0]);
      return;
    }
    
    // Store selected start date for other leave types too
    if (dates && dates[0]) {
      setSelectedStartDate(dates[0]);
    } else {
      setSelectedStartDate(null);
    }
    
    // When creating or editing maternity (parental leave for females), handle end date calculation
    if (isMaternity && dates && dates[0]) {
      if (isFirstMaternity) {
        const computedEnd = dates[0].add(3, "month").add(14, "day");
        setAutoEndDate(computedEnd);
        setCalendarRange([dates[0], computedEnd]);
        form.setFieldsValue({ dateRange: [dates[0], computedEnd] });
      } else if (isSecondMaternity) {
        // For second maternity, user can select end date, but we still need to handle it
        // The end date is not auto-calculated for second maternity, but we allow free selection
        if (dates[1]) {
          setAutoEndDate(dates[1]);
        }
      } else {
        setAutoEndDate(null);
        form.setFieldsValue({ dateRange: [] });
      }
      return;
    }
  };

  const handleRangeChange = (dates) => {
    setCalendarRange(dates || []);
    
    // When creating or editing parental leave for males, if a start date is selected,
    // immediately calculate and set the end date
    if (isParentalForMale && dates && dates[0]) {
      const computedEnd = dates[0].add(6, "day"); // 7 days total including start date
      setAutoEndDate(computedEnd);
      setCalendarRange([dates[0], computedEnd]);
      form.setFieldsValue({ dateRange: [dates[0], computedEnd] });
      // Store the selected start date for checking existing parental leaves
      setSelectedStartDate(dates[0]);
      return;
    }
    
    // Store selected start date for other leave types too
    if (dates && dates[0]) {
      setSelectedStartDate(dates[0]);
    } else {
      setSelectedStartDate(null);
    }
    
    // When creating or editing maternity (parental leave for females), handle end date calculation
    if (isMaternity && dates && dates[0]) {
      if (isFirstMaternity) {
        const computedEnd = dates[0].add(3, "month").add(14, "day");
        setAutoEndDate(computedEnd);
        setCalendarRange([dates[0], computedEnd]);
        form.setFieldsValue({ dateRange: [dates[0], computedEnd] });
      } else if (isSecondMaternity) {
        // For second maternity, user can select end date, but we still need to handle it
        // The end date is not auto-calculated for second maternity, but we allow free selection
        if (dates[1]) {
          setAutoEndDate(dates[1]);
        }
      }
      return;
    }
  };

  // Handle calendar open to navigate to first available date for second maternity
  const handleCalendarOpenChange = (open) => {
    if (open && isSecondMaternity && earliestSecondStart && !calendarRange.length) {
      // When calendar opens and no date is selected, navigate to the first available month
      // The defaultPickerValue will handle this, but we can also set it here as a fallback
      if (!defaultPickerValue || !defaultPickerValue.isSame(earliestSecondStart, 'month')) {
        setDefaultPickerValue(earliestSecondStart);
      }
    }
    
    // When creating or editing parental leave (for both males and females), temporarily clear the end date when calendar opens
    // This prevents Ant Design's RangePicker from restricting start date selection
    // based on the existing end date. The end date will be recalculated when a new start date is selected.
    if (open && (isParentalForMale || isMaternity) && calendarRange.length === 2) {
      const currentStart = calendarRange[0];
      // Clear the end date temporarily to allow selecting any start date
      setCalendarRange([currentStart]);
      form.setFieldsValue({ dateRange: [currentStart, null] });
    }
  };

  const maternityDisabledDate = useCallback(
    (current) => {
      // When editing an existing parental leave, allow the user to freely
      // choose any new start date; the end date will still be auto-calculated.
      if (isEdit) {
        return false;
      }

      if (!isMaternity || !current) return false;
      const today = dayjs().startOf("day");
      if (current.isBefore(today)) {
        return true;
      }

      if (isFirstMaternity) {
        return false;
      }

      if (isSecondMaternity) {
        if (!firstMaternityLeaveForYear) {
          return false;
        }

        const selectedStart =
          (calendarRange && calendarRange.length && calendarRange[0]) || null;

        const earliestStart = earliestSecondStart
          ? earliestSecondStart.startOf("day")
          : null;

        if (!selectedStart) {
          if (earliestStart) {
            return current.isBefore(earliestStart);
          }
          return false;
        }

        const startDay = selectedStart.startOf("day");
        const maxEnd = startDay.add(4, "month").endOf("day");

        if (current.isBefore(startDay)) {
          return true;
        }
        if (current.isAfter(maxEnd)) {
          return true;
        }
      }

      return false;
    },
    [
      calendarRange,
      earliestSecondStart,
      firstMaternityLeaveForYear,
      isFirstMaternity,
      isMaternity,
      isSecondMaternity,
      isEdit,
    ]
  );

  const rangePickerDisabled = useMemo(() => {
    if (!selectedLeaveType) {
      return true;
    }

    // When editing, don't disable the date picker even if limit is reached
    // (we're just updating an existing leave, not creating a new one)
    if (maternityUnavailable && !isEdit) {
      return true;
    }

    // For first parental leave (female) and 7-day parental leave (non-female),
    // the user should only pick the start date; we auto-calculate the end date.
    if (isFirstMaternity || isParentalForMale) {
      return [false, true];
    }

    return false;
  }, [isFirstMaternity, isParentalForMale, maternityUnavailable, selectedLeaveType, isEdit]);

  const descriptionDisabled = useMemo(
    () => !selectedLeaveType || maternitySubmissionBlocked,
    [maternitySubmissionBlocked, selectedLeaveType]
  );

  const sickDisabledDate = useCallback(
    (current) => {
      if (!isSickLeave || !current) return false;

      const earliestAllowedDay = dayjs()
        .subtract(48, "hour")
        .startOf("day");

      if (current.isBefore(earliestAllowedDay)) {
        return true;
      }

      return false;
    },
    [isSickLeave]
  );

  const rangePickerDisabledDate = useCallback(
    (current, info) => {
      if (!current) return false;
      
      // When creating or editing parental leave (for both males and females), allow any start date to be selected
      // The end date will be automatically recalculated based on the new start date
      if (isParentalForMale) {
        // Allow all dates to be selected for parental leave for males (both creating and editing)
        // This overrides any restrictions that Ant Design's RangePicker might apply
        // based on the existing range value
        return false;
      }

      if (isMaternity) {
        // For maternity (parental leave for females), allow all dates when creating or editing
        // The end date will be automatically recalculated based on the start date
        return false;
      }

      if (isSickLeave) {
        return sickDisabledDate(current);
      }

      return false;
    },
    [isParentalForMale, isMaternity, isSickLeave, sickDisabledDate]
  );

  const modalBodyStyle = useMemo(
    () => ({
      maxHeight: "80vh",
      overflowY: "auto",
      overflowX: "hidden",
      padding: "0 24px 24px",
    }),
    []
  );

  const onFinish = async (values) => {
    let [start, end] = values.dateRange || [];
    // For parental leave (female or non-female), use default description
    // since the field is hidden for this leave type.
    const description = isParentalType 
      ? "Parental leave" 
      : (values.description || "").trim();

    if (!start) {
      message.error("Please select a valid leave period.");
      return;
    }

    if (isFirstMaternity) {
      if (!autoEndDate) {
        message.error("Unable to determine the maternity leave end date.");
        return;
      }
      end = autoEndDate;
    }

    if (!end) {
      message.error("Please select a valid leave period.");
      return;
    }

    // Extended parental (maternity-style) rules apply only for female employees;
    // for non-female users, the server will enforce the dedicated parental rules.

    if (isSecondMaternity) {
      if (maternitySubmissionBlocked) {
        message.error(
          "You cannot request a second maternity leave at this time."
        );
        return;
      }
      if (earliestSecondStart && start.isBefore(earliestSecondStart)) {
        message.error(
          `Second maternity leave must start after ${earliestSecondStart.format(
            "DD MMM YYYY"
          )}.`
        );
        return;
      }
      const maxEnd = start.add(4, "month");
      if (end.isAfter(maxEnd)) {
        message.error(
          "Second maternity leave can last at most 4 months from the selected start date."
        );
        return;
      }
    }

    if (isMaternity && secondRequestDeadline && dayjs().isAfter(secondRequestDeadline)) {
      message.error(
        `Second maternity leave must be requested before ${secondRequestDeadline.format(
          "DD MMM YYYY"
        )}.`
      );
      return;
    }

    // Additional client-side rules for male (and non-female) parental leave:
    if (isParentalForMale) {
      const requestedDays = end.diff(start, "day") + 1 || 0;
      if (requestedDays !== 7) {
        message.error("Parental leave for non-female employees must be exactly 7 days.");
        return;
      }

      // Use the year of the requested leave start date, not the current year
      const requestedYear = start.year();
      const parentalLeavesForRequestedYear = (myLeaves || []).filter((leave) => {
        const typeCode = getLeaveTypeSystemCode(leave?.type);
        const statusCode = (leave?.status?.code || "").toLowerCase();
        const leaveStart = dayjs(leave.dateStart);
        return (
          typeCode === parentalTypeCode &&
          statusCode !== "rejected" &&
          leaveStart.year() === requestedYear
        );
      });

      if (parentalLeavesForRequestedYear.length >= 1) {
        message.error(
          `You have already used your parental leave for ${requestedYear}. Only one parental leave (7 days) is allowed per calendar year for non-female employees.`
        );
        return;
      }
    }

    if (isSickLeave) {
      // For editing: allow if there are existing certificates (status: 'done') or new uploads
      // For creating: require at least one certificate
      const hasCertificates = certificateFiles.length > 0;
      const hasExistingCertificates = isEdit && certificateFiles.some(file => file.status === 'done' && file.url);
      const hasNewCertificates = certificateFiles.some(file => file.originFileObj);
      
      if (!hasCertificates && (!isEdit || !hasExistingCertificates)) {
        const errorMessage =
          "Medical certificate is required for sick leave applications.";
        setFeedback({
          type: "error",
          message: errorMessage,
        });
        message.error(errorMessage);
        return;
      }

      // Calendar already enforces the 48-hour window; no additional blocking required here.
    }

    try {
      setSubmitting(true);
      setFeedback(null);

      const requestedDays = end.diff(start, "day") + 1 || 0;
      const currentBalance = user?.balanceLeft || 0;
      const remainingBalance = isMaternity
        ? currentBalance
        : currentBalance - requestedDays;

      if (isEdit && data?._id) {
        // Edit existing leave
        const leaveTypeId = values.type;
        
        if (isSickLeave) {
          const existingCertificates = certificateFiles
            .filter(file => !file.originFileObj)
            .map(file => extractCertificatePath(file))
            .filter(Boolean);
          const newCertificates = certificateFiles.filter(file => file.originFileObj);

          if (newCertificates.length > 0) {
            const formData = new FormData();
            formData.append("type", leaveTypeId);
            formData.append("description", description);
            formData.append("dateStart", start.format("MM/DD/YYYY"));
            formData.append("dateEnd", end.format("MM/DD/YYYY"));

            formData.append(
              "keepCertificates",
              JSON.stringify(existingCertificates || [])
            );

            newCertificates.forEach((file, index) => {
              const blob = file.originFileObj;
              const name = blob.name && /\.(jpe?g|png|gif|webp|heic)$/i.test(blob.name)
                ? blob.name
                : `medical-certificate-${index + 1}.jpg`;
              formData.append("medicalCertificates", blob, name);
            });

            await dispatch(
              editLeaveCurrentUser({ data: formData, id: data._id, isFormData: true })
            ).unwrap();
          } else {
            const submitData = {
              type: leaveTypeId,
              description,
              dateStart: start.format("MM/DD/YYYY"),
              dateEnd: end.format("MM/DD/YYYY"),
              keepCertificates: existingCertificates || [],
            };

            await dispatch(editLeaveCurrentUser({ data: submitData, id: data._id })).unwrap();
          }
        } else {
          const submitData = {
            type: leaveTypeId,
            description,
            dateStart: start.format("MM/DD/YYYY"),
            dateEnd: end.format("MM/DD/YYYY"),
          };

          await dispatch(editLeaveCurrentUser({ data: submitData, id: data._id })).unwrap();
        }
        
        message.success("Leave request updated successfully.");
        handleModalClose();
      } else {
        // Create new leave - use FormData for file uploads
        const formData = new FormData();
        const leaveTypeId = values.type;
        formData.append("type", leaveTypeId);
        formData.append("description", description);
        formData.append("dateStart", start.format("MM/DD/YYYY"));
        formData.append("dateEnd", end.format("MM/DD/YYYY"));

        if (isSickLeave) {
          certificateFiles.forEach((file, index) => {
            if (file.originFileObj) {
              const blob = file.originFileObj;
              const name = blob.name && /\.(jpe?g|png|gif|webp|heic)$/i.test(blob.name)
                ? blob.name
                : `medical-certificate-${index + 1}.jpg`;
              formData.append("medicalCertificates", blob, name);
            }
          });
        }

        await dispatch(addLeave(formData)).unwrap();

        if (!isMaternity && remainingBalance < 0) {
          const deficit = Math.abs(remainingBalance);
          message.success(
            `Leave request submitted. Your balance will be negative by ${Math.abs(
              deficit.toFixed(2)
            )} day(s).`
          );
        } else {
          message.success("Leave request submitted successfully.");
        }
      }

      setCertificateFiles([]);
      setSelectedLeaveType(null);
      handleModalClose();
    } catch (error) {
      // unwrap() after rejectWithValue throws the payload string; older paths may pass Axios errors
      const errorMessage =
        (typeof error === "string" ? error : null) ||
        error?.response?.data?.message ||
        (typeof error?.response?.data?.error === "string"
          ? error.response.data.error
          : error?.response?.data?.error?.message) ||
        error?.message ||
        "Failed to submit the leave request. Please try again.";
      
      // Display error in both the Alert component (inside modal) and message notification (outside modal)
      setFeedback({
        type: "error",
        message: errorMessage,
      });
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = leaveTypeStatus === "loading";

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      footer={null}
      destroyOnClose
      width={820}
      centered
      className="apply-for-leave-modal"
      bodyStyle={modalBodyStyle}
    >
      <div className="apply-for-leave-modal__header">
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          {isEdit ? "Edit Leave Request" : (title || "Apply For a Leave")}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
          {isEdit ? "Update your leave request details and keep track of your remaining balance." : "Submit a new leave request and keep track of your remaining balance."}
        </Typography.Paragraph>
      </div>

      {feedback && (
        <Alert
          type={feedback.type}
          message={feedback.message}
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setFeedback(null)}
        />
      )}

      {Array.isArray(displayableLeaveTypes) && displayableLeaveTypes.length > 0 && (
        <>
          <Typography.Text strong style={{ marginBottom: 8, display: "block" }}>
            Your approved days by leave type
          </Typography.Text>
          <Space wrap size={[8, 8]}>
            {displayableLeaveTypes.map((type) => {
              const typeName = type?.name || "Leave";
              const daysValue = leaveUsageByType[typeName] || 0;
              const isSickType = getLeaveTypeSystemCode(type) === "SICK";
              const displayValue = isSickType && daysValue >= 10 ? 10 : daysValue;
              const dayWord = displayValue === 1 ? "يوم" : "أيام";

              return (
                <span
                  key={type?._id || type?.name}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 4,
                    border: "1px solid #e8e8e8",
                    backgroundColor: "#fafafa",
                    color: "rgba(0, 0, 0, 0.85)",
                    fontSize: 13,
                  }}
                >
                  {typeName}: {displayValue} {dayWord}
                </span>
              );
            })}
          </Space>
        </>
      )}

      <Divider style={{ margin: "24px 0" }} />

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
        >
          {isMaternity && !maternityUnavailable && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                isFirstMaternity
                  ? "Parental leave benefit"
                  : "Second parental leave benefit"
              }
              description={
                isFirstMaternity
                  ? "This parental leave is granted for 3.5 months starting on your chosen start date. The end date is set automatically and the leave is not deducted from your balance. After your first parental leave ends you may request a second one that must start immediately after the first and last at most 4 months, but it needs to be submitted no later than one month before the first leave ends."
                  : "The second parental leave is also free of charge. You may select up to four months after the start date."
              }
            />
          )}

          {isSickLeave && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Sick leave policy"
              description={`You currently have ${Number(
                sickBalance
              ).toFixed(
                0
              )} sick day(s) available each year. Any sick leave beyond that balance will automatically deduct the extra days from your Vacation leave balance. Note: Dates before 48 hours ago are disabled in the calendar as sick leave must be applied for within 48 hours of the start date.`}
            />
          )}

          {isSecondMaternity && !maternityUnavailable && secondRequestDeadline && (
            <Alert
              type={isSecondRequestLate ? "error" : "warning"}
              showIcon
              style={{ marginBottom: 16 }}
              message="Second parental leave deadline"
              description={
                isSecondRequestLate
                  ? `This request must be submitted by ${secondRequestDeadline.format(
                      "DD MMM YYYY"
                    )}.`
                  : `Please submit the second parental leave before ${secondRequestDeadline.format(
                      "DD MMM YYYY"
                    )}.`
              }
            />
          )}

          {hasWaitingMaternityLeave && !isEdit && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Parental leave request pending"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>
                    You have a parental leave request waiting for approval. Please wait for it to be approved before creating a second one.
                  </div>
                  <div style={{ fontSize: '13px', color: '#595959' }}>
                    {waitingHRMaternityLeaves.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        • {waitingHRMaternityLeaves.length} waiting for HR approval
                      </div>
                    )}
                    {cancellableMaternityLeaves.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        • {cancellableMaternityLeaves.length} {isTeamLead ? 'waiting for HR approval' : 'waiting for technical approval'}
                      </div>
                    )}
                  </div>
                </div>
              }
            />
          )}

          {hasWaitingParentalLeaveForMale && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Parental leave request pending"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>
                    You have a parental leave request waiting for approval. Please wait for it to be approved or cancel it before creating a new one.
                  </div>
                  <div style={{ fontSize: '13px', color: '#595959' }}>
                    {cancellableParentalLeavesForMale.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        • {cancellableParentalLeavesForMale.length} {isTeamLead ? 'waiting for HR approval' : 'waiting for technical approval'} (can be canceled)
                      </div>
                    )}
                    {nonCancellableHRParentalLeaves.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        • {nonCancellableHRParentalLeaves.length} waiting for HR approval {isTeamLead ? '' : '(cannot be canceled by employees)'}
                      </div>
                    )}
                  </div>
                  {cancellableParentalLeavesForMale.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ marginBottom: 8 }}>
                        {isTeamLead ? (
                          <>You can cancel {cancellableParentalLeavesForMale.length > 1 ? 'one of your' : 'your'} parental leave request{cancellableParentalLeavesForMale.length > 1 ? 's' : ''} that {cancellableParentalLeavesForMale.length > 1 ? 'are' : 'is'} waiting for HR approval.</>
                        ) : (
                          <>You can cancel {cancellableParentalLeavesForMale.length > 1 ? 'one of your' : 'your'} parental leave request{cancellableParentalLeavesForMale.length > 1 ? 's' : ''} that {cancellableParentalLeavesForMale.length > 1 ? 'are' : 'is'} waiting for technical approval.</>
                        )}
                      </div>
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => {
                          handleClose();
                          navigate('/myspace');
                        }}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        Cancel {cancellableParentalLeavesForMale.length > 1 ? 'a' : 'the'} pending parental leave{cancellableParentalLeavesForMale.length > 1 ? '' : ''} and return here to create a new one
                      </Button>
                    </div>
                  )}
                  {cancellableParentalLeavesForMale.length === 0 && nonCancellableParentalLeavesForMale.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '13px', color: '#8c8c8c' }}>
                      {isTeamLead ? (
                        <>Note: You have {nonCancellableHRParentalLeaves.length} parental leave request{nonCancellableHRParentalLeaves.length > 1 ? 's' : ''} waiting for HR approval. As a team lead, you can cancel your own leaves in this status.</>
                      ) : (
                        <>Note: You have {nonCancellableHRParentalLeaves.length} parental leave request{nonCancellableHRParentalLeaves.length > 1 ? 's' : ''} waiting for HR approval, but only leaves waiting for technical approval can be canceled by employees.</>
                      )}
                    </div>
                  )}
                </div>
              }
            />
          )}

          {hasExistingParentalLeaveForYear && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Parental leave already used"
              description={
                <div>
                  You have already used your parental leave for {selectedStartDate ? selectedStartDate.year() : 'this year'}. Only one parental leave (7 days) is allowed per calendar year for non-female employees.
                </div>
              }
            />
          )}

          {maternityUnavailable && !isEdit && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Parental leave limit reached"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>
                    You have already used the available maternity leave periods.
                  </div>
                  
                  {/* Show breakdown of maternity leave statuses */}
                  <div style={{ marginBottom: 8, fontSize: '13px', color: '#595959' }}>
                    {approvedMaternityLeaves.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        • {approvedMaternityLeaves.length} approved maternity leave{approvedMaternityLeaves.length > 1 ? 's' : ''}
                      </div>
                    )}
                    {waitingHRMaternityLeaves.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        • {waitingHRMaternityLeaves.length} waiting for HR approval
                      </div>
                    )}
                    {cancellableMaternityLeaves.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        • {cancellableMaternityLeaves.length} {isTeamLead ? 'waiting for HR approval' : 'waiting for technical approval'} (can be canceled)
                      </div>
                    )}
                  </div>

                  {cancellableMaternityLeaves.length > 0 && (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        {isTeamLead ? (
                          <>You can cancel {cancellableMaternityLeaves.length > 1 ? 'one of your' : 'your'} maternity leave request{cancellableMaternityLeaves.length > 1 ? 's' : ''} that {cancellableMaternityLeaves.length > 1 ? 'are' : 'is'} waiting for HR approval.</>
                        ) : (
                          <>You can cancel {cancellableMaternityLeaves.length > 1 ? 'one of your' : 'your'} maternity leave request{cancellableMaternityLeaves.length > 1 ? 's' : ''} that {cancellableMaternityLeaves.length > 1 ? 'are' : 'is'} waiting for technical approval.</>
                        )}
                      </div>
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => {
                          handleClose();
                          navigate('/myspace');
                        }}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        Cancel {cancellableMaternityLeaves.length > 1 ? 'a' : 'the'} non-approved maternity leave{cancellableMaternityLeaves.length > 1 ? '' : ''} and return here to create a new one
                      </Button>
                    </div>
                  )}
                  
                  {cancellableMaternityLeaves.length === 0 && nonApprovedMaternityLeaves.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '13px', color: '#8c8c8c' }}>
                      {isTeamLead ? (
                        <>Note: You have {waitingHRMaternityLeaves.length} maternity leave request{waitingHRMaternityLeaves.length > 1 ? 's' : ''} waiting for HR approval. As a team lead, you can cancel your own leaves in this status.</>
                      ) : (
                        <>Note: You have {waitingHRMaternityLeaves.length} maternity leave request{waitingHRMaternityLeaves.length > 1 ? 's' : ''} waiting for HR approval, but only leaves waiting for technical approval can be canceled by employees.</>
                      )}
                    </div>
                  )}
                </div>
              }
            />
          )}

          <Form.Item
            label="Leave Type"
            name="type"
            rules={[
              {
                required: true,
                message: "Please choose the leave type.",
              },
            ]}
          >
            <Select
              placeholder="Select leave type"
              options={leaveTypeOptions}
              showSearch
              optionFilterProp="label"
              loading={isLoading}
              suffixIcon={<FieldTimeOutlined />}
              onChange={(value) => {
                const selected = leaveTypeMap.get(value) || null;
                setSelectedLeaveType(selected || null);
                if (
                  getLeaveTypeSystemCode(selected) !== "SICK"
                ) {
                  setCertificateFiles([]);
                }
                setCalendarRange([]);
                setAutoEndDate(null);
                setDefaultPickerValue(null);
                form.setFieldsValue({ dateRange: [] });
              }}
            />
          </Form.Item>

          <Form.Item
            label="Leave Dates"
            name="dateRange"
            rules={[
              {
                required: true,
                message: "Please select the leave start and end dates.",
              },
            ]}
          >
            <RangePicker
              key={isEdit && isParentalForMale ? `edit-parental-${data?._id || 'new'}` : 'default'}
              style={{ width: "100%" }}
              allowClear={!isFirstMaternity}
              format="DD MMM YYYY"
              suffixIcon={<CalendarOutlined />}
              disabled={rangePickerDisabled}
              disabledDate={rangePickerDisabledDate}
              onCalendarChange={handleCalendarChange}
              onChange={handleRangeChange}
              onOpenChange={handleCalendarOpenChange}
              defaultPickerValue={defaultPickerValue ? [defaultPickerValue, defaultPickerValue] : undefined}
              locale={enGB.DatePicker}
            />
          </Form.Item>

          {!isParentalType && (
            <Form.Item
              label="Reason / Description"
              name="description"
              rules={[
                {
                  validator: (_, value) => {
                    const trimmed = (value || "").trim();
                    if (!trimmed) {
                      return Promise.reject(
                        new Error("Please provide a description.")
                      );
                    }
                    if (trimmed.length < 10) {
                      return Promise.reject(
                        new Error(
                          "Description must be at least 10 characters long."
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Provide details about your leave request..."
                showCount
                maxLength={500}
                className="apply-for-leave-modal__textarea"
                style={{ resize: "none" }}
                disabled={descriptionDisabled}
              />
            </Form.Item>
          )}

          {isSickLeave && (
            <Form.Item label="Medical Certificates" required>
              <Upload
                multiple
                listType="picture"
                beforeUpload={handleCertificateBeforeUpload}
                onChange={handleCertificateChange}
                onRemove={handleCertificateRemove}
                fileList={certificateFiles}
                accept="image/*"
                maxCount={3}
              >
                {certificateFiles.length < 3 && (
                  <Button icon={<UploadOutlined />}>
                    Upload Certificates
                  </Button>
                )}
              </Upload>
              <Typography.Paragraph
                type="secondary"
                style={{ marginTop: 8 }}
              >
                Upload up to 3 image files (JPG, PNG). Sick leave must be
                requested within 48 hours of the start date.
              </Typography.Paragraph>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                disabled={submitting}
              >
                Reset
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={maternitySubmissionBlocked || parentalForMaleBlocked || submitting}
                style={isMaternity ? { color: '#000' } : {}}
              >
                {isEdit ? "Update Leave" : "Apply for Leave"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ApplyForLeaveModal;
